import { useEffect, useState, useMemo } from "react";

import { Form, Input, Select, Typography } from "antd";
import { FormInstance } from "antd/lib/form";

import { CompanyInfo } from "../../../../../bitmaker/schemas/Billing/CompanyInfo";

import { useTerms } from "../../../../../hooks/useTerms";

interface InvoiceNotesTermsProps {
  form: FormInstance;
  companyInfo: CompanyInfo | null;
  isEditMode: boolean;
}

const CUSTOM_TERM_VALUE = 0;

export const InvoiceNotesTerms: React.FC<InvoiceNotesTermsProps> = ({
  form,
  companyInfo,
  isEditMode,
}) => {
  const { terms, loadingTerms, fetchTermsError } = useTerms();

  // Stores the original term when editing an invoice,
  // so it can be restored if needed
  const [initialInvoiceTerm, setInitialInvoiceTerm] = useState<string | null>(
    null,
  );
  const [initialInvoiceTermId, setInitialInvoiceTermId] = useState<
    number | null
  >(null);

  const language = Form.useWatch("language", form);
  const termId = Form.useWatch("termId", form);

  useEffect(() => {
    if (!isEditMode) return;

    const currentTermId = form.getFieldValue("termId");
    const currentTerms = form.getFieldValue("terms");

    if (typeof currentTerms === "string") {
      setInitialInvoiceTerm(currentTerms);
      setInitialInvoiceTermId(currentTermId ?? null);
    }
  }, [isEditMode, form]);

  const texts = {
    notesLabel: language === "es" ? "Notas" : "Notes",
    termsLabel: language === "es" ? "Términos" : "Terms",
  };

  const termOptions = useMemo(() => {
    // If there was an error fetching terms, only allow "Other"
    if (fetchTermsError || terms.length === 0) {
      const options = [];
      if (isEditMode) {
        const currentTermId = form.getFieldValue("termId");

        if (currentTermId != null && currentTermId !== CUSTOM_TERM_VALUE) {
          options.push({
            value: currentTermId,
            label: "Current term",
          });
        }
      }
      options.push({
        value: CUSTOM_TERM_VALUE,
        label: "Other",
      });
      return options;
    }

    // Normal case: map fetched terms
    const options = terms.map((term) => ({
      value: term.id,
      label: term.name,
    }));

    options.push({
      value: CUSTOM_TERM_VALUE,
      label: "Other",
    });

    return options;
  }, [terms, fetchTermsError, isEditMode, form]);

  const isCustomTermSelected = termId === CUSTOM_TERM_VALUE;

  const isReadOnly = !isCustomTermSelected || loadingTerms;

  const handleTermSelect = (selectedId: number) => {
    // Other (custom term)
    if (selectedId === CUSTOM_TERM_VALUE) {
      const shouldRestoreCustomTerm =
        isEditMode &&
        (initialInvoiceTermId === CUSTOM_TERM_VALUE ||
          initialInvoiceTermId == null);
      form.setFieldsValue({
        termId: CUSTOM_TERM_VALUE,
        terms: shouldRestoreCustomTerm ? initialInvoiceTerm : "",
      });
      return;
    }

    // Error or no terms available (restore initial term if editing)
    if (isEditMode && (fetchTermsError || terms.length === 0)) {
      form.setFieldsValue({
        termId: selectedId,
        terms: initialInvoiceTerm,
      });
      return;
    }

    // Standard term
    const selectedTerm = terms.find((t) => t.id === selectedId);

    form.setFieldsValue({
      termId: selectedId,
      terms: selectedTerm?.content ?? "",
    });
  };

  const handleTermClear = () => {
    form.setFieldsValue({ termId: null, terms: null });
  };

  useEffect(() => {
    if (isEditMode) return;
    if (loadingTerms || !companyInfo?.defaultTermId) return;
    if (form.getFieldValue("termId")) return;

    const defaultTerm = terms.find((t) => t.id === companyInfo.defaultTermId);

    if (defaultTerm) {
      form.setFieldsValue({
        termId: defaultTerm.id,
        terms: defaultTerm.content,
      });
    }
  }, [loadingTerms, terms, companyInfo, form, isEditMode]);

  const termsPlaceholder = fetchTermsError
    ? isCustomTermSelected
      ? "Write your custom terms here."
      : "Select “Other” to write custom terms."
    : loadingTerms
      ? "Loading terms..."
      : termId == null
        ? "Write your custom term here (available when “Other” is selected)."
        : termId !== CUSTOM_TERM_VALUE
          ? "This term is read-only."
          : "Write your custom term here.";

  return (
    <div className="col-span-12 grid grid-cols-12 gap-4 mt-12 mb-8">
      {/* Divider */}
      <div className="border-t col-span-10"></div>

      {/* Notes */}
      <div className="col-span-10">
        <h3 className="font-bold text-green-600 mb-2">{texts.notesLabel}</h3>
        <Form.Item name="notes" className="mb-0">
          <Input.TextArea
            rows={2}
            placeholder="Optional notes for the client."
            style={{ resize: "none" }}
          />
        </Form.Item>
      </div>

      {/* Terms */}
      <div className="col-span-10">
        <h3 className="font-bold text-green-600 mb-2">{texts.termsLabel}</h3>

        {fetchTermsError && (
          <Typography.Text type="danger" className="block mb-1 text-sm">
            {isEditMode
              ? "Terms could not be loaded. You can keep the current value or use a custom term (“Other”)."
              : "Terms could not be loaded. You can only use a custom term (“Other”)."}
          </Typography.Text>
        )}

        {terms.length === 0 && !fetchTermsError && (
          <Typography.Text type="secondary" className="block mb-1 text-sm">
            {isEditMode
              ? "No predefined terms are available. You can keep the current value or use a custom term (“Other”)."
              : "No predefined terms are available. You can use a custom term (“Other”)."}
          </Typography.Text>
        )}

        <Form.Item
          name="termId"
          className="mb-2"
          normalize={(value) => (value === undefined ? null : value)}
        >
          <Select
            allowClear
            placeholder={
              fetchTermsError
                ? "Only custom terms are available"
                : loadingTerms
                  ? "Loading terms..."
                  : terms.length === 0
                    ? "Only custom terms are available"
                    : "Select a term or choose “Other” to write a custom one"
            }
            options={termOptions}
            loading={loadingTerms}
            disabled={loadingTerms}
            onSelect={handleTermSelect}
            onClear={handleTermClear}
          />
        </Form.Item>

        <Form.Item name="terms" className="mb-0">
          <Input.TextArea
            rows={8}
            placeholder={termsPlaceholder}
            style={{ resize: "none" }}
            readOnly={isReadOnly}
          />
        </Form.Item>
      </div>
    </div>
  );
};
